/**
 * Fetches registered-market context for a listing, a form draft, or a district.
 *
 * Deliberately forgiving: it never throws, never surfaces an error state, and
 * resolves to `available: false` when there is nothing to show. A market card is
 * additional context on a page that has to work without it.
 */

import { useEffect, useState } from 'react';
import type { MarketCard } from '../types/marketData';
import {
  getDistrictMarketCard,
  getPropertyMarketCard,
  lookupMarketCard,
  type MarketLookupInput,
} from '../services/marketDataService';

export type UseMarketCardInput =
  | { kind: 'property'; propertyId?: string | null }
  | { kind: 'district'; placeId?: string | null }
  | ({ kind: 'lookup' } & MarketLookupInput);

interface UseMarketCardResult {
  card: MarketCard | null;
  loading: boolean;
}

/** A lookup needs somewhere to look — a district name or a coordinate pair. */
function lookupIsUsable(input: MarketLookupInput): boolean {
  return Boolean(input.district || (typeof input.lat === 'number' && typeof input.lng === 'number'));
}

export function useMarketCard(input: UseMarketCardInput, enabled = true): UseMarketCardResult {
  const [card, setCard] = useState<MarketCard | null>(null);
  const [loading, setLoading] = useState(false);

  // Serialising the input keeps the effect from re-running on every render just
  // because the caller built a fresh object literal.
  const key = JSON.stringify(input);

  useEffect(() => {
    if (!enabled) {
      setCard(null);
      return undefined;
    }

    const parsed = JSON.parse(key) as UseMarketCardInput;
    const controller = new AbortController();
    let active = true;

    const run = async () => {
      let result: MarketCard | null = null;

      if (parsed.kind === 'property') {
        if (!parsed.propertyId) return;
        setLoading(true);
        result = await getPropertyMarketCard(parsed.propertyId, controller.signal);
      } else if (parsed.kind === 'district') {
        if (!parsed.placeId) return;
        setLoading(true);
        result = await getDistrictMarketCard(parsed.placeId, controller.signal);
      } else {
        const { kind: _kind, ...rest } = parsed;
        if (!lookupIsUsable(rest)) return;
        setLoading(true);
        result = await lookupMarketCard(rest, controller.signal);
      }

      if (active) {
        setCard(result);
        setLoading(false);
      }
    };

    run();

    return () => {
      active = false;
      controller.abort();
    };
  }, [key, enabled]);

  return { card, loading };
}

export default useMarketCard;
